import { HISTORY_ACTION_MODE, useEditor } from '@candulabs/craft';
import React, { useMemo } from 'react';

import { ColorPicker, ValueGroup } from '@candu-ai/craft/ui';
import { useGenerateInteractionLabelName } from 'craft/containers/Actions/useGenerateInteractionLabelName';
import { Section } from 'craft/containers/Toolbox/components';
import { Spacing } from 'craft/containers/Toolbox/sections';
import { CssSelectorId, useCss, useMultiNodes } from 'craft/hooks';
import { Action, CATEGORIES, CustomNodeData } from 'craft/types';

// TODO: copy/paste from base.ts to use as defaults as these are hard-coded values and not part of the styleguide
// cannot be pulled in with our current setup, to be moved into styleguide in the future but for now hacking it!
const DEFAULTS = {
  'copy-text-wrapper': {
    padding: '8px',
  },
  'copy-text-svg': {
    fill: '#7e86a0',
    'stroke-width': '1px',
  },
};

interface Props {
  nodeIds: string[];
}

export const CopyText = ({ nodeIds }: Props) => {
  const {
    hasCopy,
    actions: { setProps },
  } = useMultiNodes(nodeIds, (node) => ({ hasCopy: node?.data.props.hasCopy }));
  const { actions } = useEditor();
  const generateInteractionLabelName = useGenerateInteractionLabelName(nodeIds);

  const { css, setCss } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.CopyTextSVG,
  });

  const defaultValues = useMemo(
    () => ({
      [CssSelectorId.CopyText]: {
        paddingTop: DEFAULTS[CssSelectorId.CopyText].padding,
        paddingRight: DEFAULTS[CssSelectorId.CopyText].padding,
        paddingLeft: DEFAULTS[CssSelectorId.CopyText].padding,
        paddingBottom: DEFAULTS[CssSelectorId.CopyText].padding,
      },
      [CssSelectorId.CopyTextSVG]: {
        strokeWidth: parseFloat(DEFAULTS[CssSelectorId.CopyTextSVG]['stroke-width']),
        fill: DEFAULTS[CssSelectorId.CopyTextSVG].fill,
      },
    }),
    [],
  );

  const onToggleCopy = () => {
    const newState = !hasCopy;
    setProps({ hasCopy: newState });

    if (!newState) {
      // reset fill when hasCopy is removed
      setCss({ fill: undefined });
    }

    // if enabled, add an action
    actions.setCustom(
      nodeIds,
      (custom: CustomNodeData) => {
        if (newState) {
          // eslint-disable-next-line no-param-reassign
          custom.actions = [
            {
              category: CATEGORIES.RICHTEXT,
              eventName: generateInteractionLabelName('Copy Text'),
            } as Action,
          ];
        } else {
          // Can only have one type of interaction, if hasCopy is removed, remove the existing action
          // eslint-disable-next-line no-param-reassign
          custom.actions = undefined;
        }
      },
      HISTORY_ACTION_MODE.MERGE,
    );
  };

  return (
    <Section name="Enable Copy" isExpanded={hasCopy} onToggle={onToggleCopy}>
      <ValueGroup>
        <ColorPicker
          label="Icon Color"
          isInGroup
          defaultValue={defaultValues[CssSelectorId.CopyTextSVG].fill}
          value={css?.fill || defaultValues[CssSelectorId.CopyTextSVG].fill}
          onChange={(newColor) => {
            setCss({ fill: newColor });
          }}
        />
      </ValueGroup>
      <ValueGroup>
        <Spacing
          nodeIds={nodeIds}
          schema={{
            cssSelectorId: CssSelectorId.CopyText,
            hasMargin: false,
            hasPadding: true,
          }}
          cssDefaults={defaultValues[CssSelectorId.CopyText]}
        />
      </ValueGroup>
    </Section>
  );
};
